import type { ReactNode } from 'react'

import type { CharacteristicValueType } from '@/types'

export type PriorityItemId = string | number

export type PriorityListType = 'char' | 'value'

export type PriorityItem = {
  id?: string
  code?: number
  value?: string
  isMain?: boolean
  priority?: number
}

export type PriorityUnitOfMeasurement = {
  id?: string
  code?: string
  description?: string
}

export type PriorityValueOption = {
  value: string
  isMain?: boolean
  count?: number
  priority?: number
}

export type PriorityCharacteristic = {
  code: number
  name: string
  isMain?: boolean
  type?: CharacteristicValueType
  priority?: number
  options?: PriorityValueOption[]
  min?: number
  max?: number
  unitOfMeasurementDto?: PriorityUnitOfMeasurement | null
  slotsCount?: number
}

export type PriorityConfigItem = PriorityCharacteristic | PriorityValueOption

export type PriorityRowData = PriorityItem & {
  name?: string
  type?: string
  count?: number
  options?: PriorityValueOption[]
  unitOfMeasurementDto?: PriorityUnitOfMeasurement | null
}

export type PriorityRowProps<T extends PriorityItem> = {
  data: T
  index: number
  type?: PriorityListType
  isConfigured: boolean
  isChanged?: boolean
  hasValueChanges?: boolean
  isOverlay?: boolean
  dragProps?: object
  onReset?(id: PriorityItemId): void
  onPrioritize?(id: PriorityItemId): void
  onOpenValues?(item: T): void
}

export type PriorityManagerProps<T extends PriorityItem> = {
  items?: T[]
  initialItems?: T[]
  onDataChange(items: T[]): void
  type?: PriorityListType
  onOpenValues?(item: T): void
  onUndo(): void
  onRedo(): void
  canUndo: boolean
  canRedo: boolean
  valueChangesMap?: Record<number, boolean>
  renderRow?(props: PriorityRowProps<T>): ReactNode
  mainTitle?: ReactNode
  othersTitle?: ReactNode
  mainEmptyText?: ReactNode
  othersEmptyText?: ReactNode
  canEditPriority?: boolean
}

export type PriorityCategory = {
  id: string
  parentId?: string
  code: number
  description: string
  priority?: number
}

export type PriorityCategoryTree = {
  category: PriorityCategory
  characteristics?: PriorityCharacteristic[]
  childCategories?: PriorityCategoryTree[]
}

export type PriorityCategoryTreesResponse = {
  trees?: PriorityCategoryTree[]
}

export type PriorityHistoryEntry = {
  initial: PriorityConfigItem[]
  present: PriorityConfigItem[]
  past: PriorityConfigItem[][]
  future: PriorityConfigItem[][]
}

export type CategoryPriorityUpdate = {
  categoryId: string
  characteristicCode: number
  isMain: boolean
  priority?: number
}

export type ValuePriorityUpdate = {
  characteristicCode: number
  value: string
  isMain: boolean
  priority?: number
}

export type PriorityValuesModalContent = PriorityCharacteristic & {
  categoryId: string
  options: PriorityValueOption[]
}

export const isPriorityCharacteristic = (
  item: PriorityConfigItem
): item is PriorityCharacteristic => 'name' in item

export const isPriorityValueOption = (
  item: PriorityConfigItem
): item is PriorityValueOption => !('name' in item)
